import { createContext, useCallback, useContext, useMemo, useSyncExternalStore, type ReactNode } from 'react';
import { SignaAuth, readSignaConfig, type SignaUser } from './signaAuth';
import { ApiClient } from './apiClient';

// La sesión, para React. Una sola instancia de `SignaAuth` y una sola de `ApiClient` por app: el
// token de acceso vive en memoria DENTRO de `SignaAuth`, así que dos instancias serían dos sesiones
// distintas refrescando el mismo refresh token y quemándoselo mutuamente por rotación.

export interface SessionValue {
  user: SignaUser | null;
  isAuthenticated: boolean;
  api: ApiClient;
  /** Mensaje EXACTO de configuración faltante («VITE_SIGNA_ISSUER is not configured»). */
  configError: string | null;
  /** ¿Tiene alguno de estos roles de Signa? La UI no inventa un modelo de roles propio. */
  hasRole: (...roles: string[]) => boolean;
  login: (returnTo?: string) => Promise<void>;
  completeLogin: (search: string) => Promise<string>;
  logout: () => void;
}

const SessionContext = createContext<SessionValue | null>(null);

interface Wiring {
  auth: SignaAuth | null;
  api: ApiClient | null;
  configError: string | null;
}

/**
 * FAIL-CLOSED (policies/deployment.md §0): si falta una variable de ambiente, la app NO arranca con
 * un default plausible — dice qué falta, con el nombre de la variable. Un fallback hardcodeado es
 * cómo qa termina hablándole al Signa de producción sin que nadie se entere.
 */
function wire(): Wiring {
  try {
    const config = readSignaConfig();
    const auth = new SignaAuth(config);
    const api = new ApiClient({
      origin: config.apiOrigin,
      getAccessToken: () => auth.getAccessToken(),
      refreshAccessToken: () => auth.refresh(),
      onSessionLost: () => auth.logout(),
    });
    return { auth, api, configError: null };
  } catch (err) {
    return { auth: null, api: null, configError: err instanceof Error ? err.message : String(err) };
  }
}

export function SignaSessionProvider({ children }: { children: ReactNode }) {
  const { auth, api, configError } = useMemo(wire, []);

  // Re-render cuando la sesión cambia (login, refresco, logout). El snapshot es un contador que
  // `SignaAuth` incrementa al emitir: comparar el objeto usuario sería comparar identidades.
  const subscribe = useCallback(
    (listener: () => void) => (auth ? auth.subscribe(listener) : () => {}),
    [auth],
  );
  const getSnapshot = useCallback(() => (auth?.isAuthenticated ? auth.user : null), [auth]);
  const user = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  const value = useMemo<SessionValue>(() => ({
    user,
    isAuthenticated: Boolean(user),
    api: api as ApiClient,
    configError,
    hasRole: (...roles: string[]) => Boolean(user?.roles.some((role) => roles.includes(role))),
    login: async (returnTo?: string) => {
      if (!auth) return;
      window.location.assign(await auth.beginLogin(returnTo));
    },
    completeLogin: (search: string) => {
      if (!auth) return Promise.reject(new Error(configError ?? 'Identidad no configurada'));
      return auth.completeLogin(search);
    },
    logout: () => auth?.logout(),
  }), [user, api, auth, configError]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession necesita estar dentro de <SignaSessionProvider>');
  return ctx;
}
